import { isEmpty, noop } from 'lodash';
import { useCallback, useMemo } from 'react';

import { ICD10Disease, ICD10DiseaseQuery } from '@dreryk/egabinet-icd-dictionary-frontend-api';

import { CqrsQueryPerformResult, performCqrsQuerySearch } from '@dreryk/command-query-react-common';
import { Bean, ValidationRules } from '@dreryk/react-common';
import {
   Autosuggest,
   StrongText,
   Text,
   TextSeparator,
   TextSeparatorType,
   useInteractiveTableContext,
} from '@dreryk/react-components';
import { TextPattern } from '@dreryk/watermelon';

import { visitDetailsTranslationKeys } from '../../../../../translations';
import { dispatchVisitDiagnosisPrependEvent } from '../../../VisitFormSynchronization.event';
import { useVisitFormSyncing } from '../../../VisitFormSynchronization.hooks';
import { DiagnosesController, DiagnosisTableBean } from '../DiagnosesFormTab.types';

const icd10DiseasesSearchCreator = (dictionaryValidOn: Date) => {
   return (searchText: string | null) => {
      return async (): CqrsQueryPerformResult<ICD10Disease[]> =>
         performCqrsQuerySearch<ICD10Disease>(() => {
            const query = new ICD10DiseaseQuery();
            query.codeOrName = TextPattern.anywhereIgnoreCase(searchText ?? undefined);
            query.dictionaryValidOn = dictionaryValidOn;
            return query;
         });
   };
};

const labelExtractor = (): string => {
   // we don't want to display anything after selection
   return '';
};

const itemRenderer = (item: ICD10Disease): JSX.Element => {
   return (
      <TextSeparator separator={TextSeparatorType.SPACE}>
         <StrongText testId={'name'}>{item.name}</StrongText>
         <Text testId={'code'}>{`(${item.code})`}</Text>
      </TextSeparator>
   );
};

type DiagnosesInteractiveTableSelectorProps = Pick<DiagnosesController, 'register'> & {
   readonly dictionaryValidOn: Date;
   readonly required: boolean;
};

export const DiagnosesInteractiveTableSelector = (props: DiagnosesInteractiveTableSelectorProps): JSX.Element => {
   const { getValues } = useInteractiveTableContext<DiagnosisTableBean>();
   const visitFormIsSyncing = useVisitFormSyncing();
   const validationRules = useMemo<ValidationRules<Bean>>(() => {
      return {
         required: props.required && isEmpty(getValues()) && !visitFormIsSyncing,
      };
   }, [getValues, props.required, visitFormIsSyncing]);

   const handleSelectionChange = useCallback((selectedDisease: ICD10Disease | null): void | boolean => {
      if (!selectedDisease) {
         return noop();
      }

      return dispatchVisitDiagnosisPrependEvent({
         code: selectedDisease.code,
         name: selectedDisease.name,
      });
   }, []);

   return (
      <Autosuggest<ICD10Disease>
         placeholder={visitDetailsTranslationKeys.diagnoses.selectorPlaceholder}
         horizontal={false}
         controller={props.register('diagnoses.search', validationRules)}
         defaultValue={null}
         searchCreator={icd10DiseasesSearchCreator(props.dictionaryValidOn)}
         itemRenderer={itemRenderer}
         onSelectionChange={handleSelectionChange}
         labelExtractor={labelExtractor}
         keyProperty={'code'}
         minChars={2}
         testId={'diagnosesSelector'}
         resetAfterSelection
      />
   );
};
